import { lazy, Suspense } from "react";
import { ArrowUpRight } from "lucide-react";
import Reveal from "../components/Reveal";
import { Seo } from "../components/Seo";
import { useT } from "../i18n/I18nProvider";

const HeroModel3D = lazy(() => import("../components/portfolio/HeroModel3D"));

const MMF_URL = "https://www.myminifactory.com/users/MysteriousStew";

const FEATURED = [
  { name: "Rabenstadt Knights", img: "/portfolio/knights_cut.webp", span: "md:col-span-7" },
  { name: "Rat Abomination", img: "/portfolio/abomination_cut.webp", span: "md:col-span-5" },
  { name: "Mother of the Swarm", img: "/portfolio/swarm_cut.webp", span: "md:col-span-5" },
  { name: "Modular Mark14 Tank", img: "/portfolio/tank_cut.webp", span: "md:col-span-7" },
];

export default function Portfolio() {
  const t = useT();

  return (
    <>
      <Seo page="journal" titleOverride="Portfolio — Miniature 3D · Mysterious Stew" descriptionOverride={t("portfolio.header.paragraph")} />

      {/* HEADER */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 pt-16 md:pt-24 pb-12 md:pb-16 grid lg:grid-cols-12 gap-8 items-end">
        <div className="lg:col-span-8">
          <Reveal>
            <div className="overline mb-4 md:mb-6">{t("portfolio.header.overline")}</div>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-display text-5xl sm:text-7xl md:text-8xl lg:text-[9vw] leading-[0.92] tracking-tightest">
              {t("portfolio.header.title1")}
              <br />
              <span className="font-editorial text-ink-700">{t("portfolio.header.titleEditorial")}</span>
              {t("portfolio.header.titleEnd")}
            </h1>
          </Reveal>
        </div>
        <div className="lg:col-span-4">
          <Reveal delay={160}>
            <p className="text-[15px] md:text-[16px] text-ink-500 leading-relaxed">{t("portfolio.header.paragraph")}</p>
            <a href={MMF_URL} target="_blank" rel="noreferrer" className="btn-pill mt-6" data-testid="portfolio-mmf-link">
              {t("mmf.visit")}
              <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.6} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* 3D VIEWER */}
      <section className="border-y border-ink-200" data-testid="portfolio-3d">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 py-10 md:py-14 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3 flex flex-col justify-between">
            <Reveal>
              <div className="overline mb-3">{t("mmf.viewerLabel")}</div>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight">
                {t("mmf.viewerTitle")} <span className="font-editorial text-ink-700">{t("mmf.viewerTitleEditorial")}</span>
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <div className="mt-6 lg:mt-0">
                <div className="text-[14px] text-ink-500">Mutant Rat · Ratmoles</div>
                <div className="overline mt-2 text-ink-300">{t("mmf.viewerHint")}</div>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-9">
            <Reveal delay={80}>
              <div className="h-[420px] md:h-[560px] cursor-grab active:cursor-grabbing" data-testid="portfolio-3d-wrap">
                <Suspense fallback={<div className="h-full flex items-center justify-center overline text-ink-300">{t("mmf.viewerLoading")}</div>}>
                  <HeroModel3D />
                </Suspense>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FEATURED MODELS */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 py-14 md:py-24" data-testid="portfolio-featured">
        <Reveal>
          <div className="overline mb-10">{t("mmf.featuredLabel")}</div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-14">
          {FEATURED.map((m, i) => (
            <Reveal key={m.name} delay={i * 60} className={m.span}>
              <div data-testid={`portfolio-model-${i}`}>
                <img src={m.img} alt={m.name} loading="lazy" className="w-full max-h-[520px] object-contain" />
                <div className="mt-5 flex items-baseline gap-4 border-t border-ink-200 pt-4">
                  <span className="overline text-ink-300">0{i + 1}</span>
                  <h3 className="font-display text-xl md:text-2xl tracking-tight">{m.name}</h3>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 pb-16">
        <Reveal className="card-flat p-8 md:p-16 grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-8">
            <div className="overline mb-3">{t("portfolio.cta.overline")}</div>
            <h3 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tightest">
              {t("portfolio.cta.title1")}
              <br /> {t("portfolio.cta.title2")}{" "}
              <span className="font-editorial text-ink-700">{t("portfolio.cta.titleEditorial")}</span>
              {t("portfolio.cta.titleEnd")}
            </h3>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <a href={MMF_URL} target="_blank" rel="noreferrer" className="btn-pill" data-testid="portfolio-cta">
              {t("mmf.visit")}
              <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.6} />
            </a>
          </div>
        </Reveal>
      </section>
    </>
  );
}
